// Location detection utilities
const LOCATION_SERVICES = [
  'https://ipapi.co/json/',
  'https://ipinfo.io/json',
  'https://ip-api.com/json/'
];

// Shared input type detection functions
export const detectInputType = (input) => {
  const trimmedInput = input.trim();
  
  // Check if it's a digipin (format: XXX-XXX-XXXX according to official DIGIPIN specification)
  // Uses characters: 2,3,4,5,6,7,8,9,C,J,K,L,M,P,F,T
  const isDigipin = /^[2-9CJKLMFPT]{3}-[2-9CJKLMFPT]{3}-[2-9CJKLMFPT]{4}$/i.test(trimmedInput);
  if (isDigipin) {
    return 'digipin';
  }
  
  // Check if it's lat/lng coordinates (format: lat,lng)
  const isLatLng = /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/.test(trimmedInput);
  if (isLatLng) {
    return 'latlng';
  }
  
  // Default to address
  return 'address';
};

// Test cases for detectInputType:
// detectInputType('39J-49L-L8T4') -> 'digipin' (Dak Bhawan example from official doc)
// detectInputType('28.6139,77.2090') -> 'latlng'
// detectInputType('-33.8688, 151.2093') -> 'latlng'
// detectInputType('New Delhi, India') -> 'address'
// detectInputType('') -> 'address'

export const parseLatLng = (input) => {
  const trimmedInput = input.trim();
  const coords = trimmedInput.split(',').map(coord => parseFloat(coord.trim()));
  if (coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
    return { lat: coords[0], lng: coords[1] };
  }
  return null;
};

// Test cases for parseLatLng:
// parseLatLng('28.6139,77.2090') -> { lat: 28.6139, lng: 77.2090 }
// parseLatLng('-33.8688, 151.2093') -> { lat: -33.8688, lng: 151.2093 }
// parseLatLng('invalid') -> null

export const validateDigipin = (digipin) => {
  const trimmedDigipin = digipin.trim();
  // Official DIGIPIN format: XXX-XXX-XXXX using characters 2,3,4,5,6,7,8,9,C,J,K,L,M,P,F,T
  return /^[2-9CJKLMFPT]{3}-[2-9CJKLMFPT]{3}-[2-9CJKLMFPT]{4}$/i.test(trimmedDigipin);
};

// Test cases for validateDigipin:
// validateDigipin('39J-49L-L8T4') -> true (Dak Bhawan example from official doc)
// validateDigipin('invalid') -> false

// Enhanced IP-based location detection with multiple services
export const detectUserLocationByIP = async () => {
  for (const service of LOCATION_SERVICES) {
    try {
      console.log(`Trying IP location service: ${service}`);
      
      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch(service, {
        signal: controller.signal,
        mode: 'cors'
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        console.log(`IP service response:`, data);
        
        // Handle different response formats from different services
        let lat = null;
        let lng = null;
        let country_code = null;
        let country_name = null;
        
        // ipapi.co format
        if (data.latitude && data.longitude) {
          lat = parseFloat(data.latitude);
          lng = parseFloat(data.longitude);
          country_code = data.country_code || data.country;
          country_name = data.country_name || data.country;
        }
        // ipinfo.io format
        else if (data.loc) {
          const [latStr, lngStr] = data.loc.split(',');
          lat = parseFloat(latStr);
          lng = parseFloat(lngStr);
          country_code = data.country;
          country_name = data.country;
        }
        // ip-api.com format
        else if (data.lat && data.lon) {
          lat = parseFloat(data.lat);
          lng = parseFloat(data.lon);
          country_code = data.countryCode;
          country_name = data.country;
        }
        
        if (lat && lng) {
          const result = {
            lat,
            lng,
            country_code,
            country_name
          };
          console.log(`IP location detection successful:`, result);
          return result;
        }
      } else {
        console.warn(`IP service ${service} returned status: ${response.status}`);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn(`Location service ${service} timed out`);
      } else {
        console.warn(`Location service ${service} failed:`, error);
      }
    }
  }
  
  console.log('All IP location services failed');
  return null; // All services failed
};

// Note: Location sharing status tracking has been removed

// Request explicit location permission from browser
export const requestUserLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      console.log('Geolocation not supported by browser');
      reject(new Error('Geolocation not supported'));
      return;
    }
    
    console.log('Requesting browser geolocation permission...');
    
         navigator.geolocation.getCurrentPosition(
       (position) => {
         console.log('Browser geolocation successful:', position);
         console.log('Geolocation accuracy:', position.coords.accuracy);
         console.log('Geolocation timestamp:', position.timestamp);
         const result = {
           lat: position.coords.latitude,
           lng: position.coords.longitude,
           country_code: null, // Will need to be determined separately
           country_name: null
         };
         console.log('Resolved browser location:', result);
         resolve(result);
       },
             (error) => {
         console.log('Browser geolocation failed:', error.message);
         console.log('Error code:', error.code);
         console.log('Full error object:', error);
         switch(error.code) {
           case error.PERMISSION_DENIED:
             console.log('User denied geolocation permission');
             break;
           case error.POSITION_UNAVAILABLE:
             console.log('Location information unavailable');
             break;
           case error.TIMEOUT:
             console.log('Geolocation request timed out');
             break;
           default:
             console.log('Unknown geolocation error');
             break;
         }
         reject(error);
       },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  });
};

// Simplified location detection function with GPS -> IP fallback
export const detectUserLocation = async () => {
  console.log('=== detectUserLocation called ===');
  console.log('Starting location detection...');
  
  try {
    // Priority 1: Try browser geolocation first
    console.log('Attempting browser geolocation...');
    try {
      const browserLocation = await requestUserLocation();
      console.log('GPS location detection successful:', browserLocation);
      return {
        ...browserLocation,
        source: 'gps'
      };
    } catch (error) {
      console.log('GPS location failed, falling back to IP-based location:', error.message);
      console.log('Error details:', error);
    }
    
    // Priority 2: IP-based location fallback
    console.log('Using IP-based location...');
    const ipLocation = await detectUserLocationByIP();
    if (ipLocation) {
      console.log('IP-based location detection successful:', ipLocation);
      return {
        ...ipLocation,
        source: 'ip_fallback'
      };
    }
    
    // All methods failed
    console.log('No location detection method succeeded');
    throw new Error('All location detection methods failed');
    
  } catch (error) {
    console.log('All location detection methods failed');
    throw error; // Re-throw the error to let the caller handle it
  }
};

// Check if location is in India
export const isLocationInIndia = (location) => {
  console.log('isLocationInIndia called with:', location);
  return location && location.country_code === 'IN';
};

// Get default center based on location
export const getDefaultMapCenter = (location) => {
  if (location && location.lat && location.lng) {
    return { lat: location.lat, lng: location.lng };
  }
  
  // No default center - let the caller handle this
  return null;
};
